import {
  DataType,
  DataBlockDictionaryDto,
  DataBlockDictionaryItemDto,
  DataBlockVariant,
} from '@lmt/shared-types';
import i18next from 'i18next';
import { snakeCase } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export function useLocalizeDataTypes(): Record<DataType, string> {
  const dataTypes = useDataTypesList();
  return useMemo(() => {
    const result: Record<DataType, string> = {} as any;
    dataTypes.forEach((item) => {
      result[item.value] = item.displayValue;
    });
    return result;
  }, [dataTypes]);
}
export function useDataTypesList(options?: {
  usage: 'dataBlockTableColumn' | 'dataBlockField' | 'customDictionaryColumn';
}) {
  const usage = options?.usage ?? 'dataBlockField';
  const i18next = useTranslation();
  return useMemo(
    () =>
      getDataTypesLocalizationsInternal(i18next.i18n).filter((x) => {
        if (usage === 'dataBlockTableColumn') {
          return (
            x.value !== DataType.Table &&
            x.value !== DataType.TimeOnly &&
            x.value !== DataType.Undefined
          );
        } else if (usage === 'customDictionaryColumn') {
          return x.value === DataType.Text || x.value === DataType.Dictionary;
        } else if (usage === 'dataBlockField') {
          return x.value !== DataType.MultilineText;
        }
      }),
    [i18next.i18n.language],
  );
}

export function localizeDataType(dataType: DataType) {
  const localizations = getDataTypesLocalizationsInternal(i18next);
  return localizations.find((x) => x.value === dataType)?.displayValue;
}
function getDataTypesLocalizationsInternal(i18n: any) {
  return [
    {
      value: DataType.Number,
      displayValue: i18n.t('data_blocks.data_types.number'),
    },
    {
      value: DataType.Text,
      displayValue: i18n.t('data_blocks.data_types.text'),
    },
    {
      value: DataType.Boolean,
      displayValue: i18n.t('data_blocks.data_types.boolean'),
    },
    {
      value: DataType.DateTime,
      displayValue: i18n.t('data_blocks.data_types.date_time'),
    },
    {
      value: DataType.DateOnly,
      displayValue: i18n.t('data_blocks.data_types.date_only'),
    },
    {
      value: DataType.TimeOnly,
      displayValue: i18n.t('data_blocks.data_types.time_only'),
    },
    {
      value: DataType.Dictionary,
      displayValue: i18n.t('data_blocks.data_types.dictionary'),
    },
    {
      value: DataType.Table,
      displayValue: i18n.t('data_blocks.data_types.table'),
    },
    {
      value: DataType.MultilineText,
      displayValue: i18n.t('data_blocks.data_types.multiline_text'),
    },
  ];
}

export const onlyActiveDictionaries = (x: DataBlockDictionaryDto[]) =>
  x.filter((z) => !z.isDeleted);
export const onlyActiveDictionaryItems = (x: DataBlockDictionaryItemDto[]) =>
  x.filter((z) => !z.isDeleted);

export type DataBlockFieldFormData = {
  name: string;
  type: number;
  dictionaryId?: number;
  tableColumns: TableColumn[];
  isOptionAllowed?: boolean;
};

type TableColumn = {
  id: number | null;
  name: string;
  dataType: number;
  dictionaryId: number | null;
  dictionaryColumnId: number | null;
  isOptionAllowed: boolean | null;
  isSingleValue: boolean | null;
};

/*
We use special value (0) for default DictionaryColumn (Value), since DropDownLists can't handle null values
 */
export const ValueDictionaryColumn = 0;

export function defaultTableColumn(): TableColumn {
  return {
    id: undefined!,
    dataType: DataType.Undefined,
    dictionaryColumnId: undefined!,
    dictionaryId: undefined!,
    name: '',
    isOptionAllowed: true,
    isSingleValue: false,
  };
}

export function getFieldLocalizationKey(
  variant: DataBlockVariant,
  type: string,
  name: string,
): string {
  if (variant === DataBlockVariant.SharedFields) {
    return type.toLowerCase() + '.shared.' + name.toLowerCase();
  } else {
    return (
      snakeCase(DataBlockVariant[variant]) +
      '.' +
      snakeCase(type) +
      '.' +
      name.toLowerCase()
    );
  }
}
